import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, PaperPlaneRight, Lifebuoy } from "@phosphor-icons/react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

export function AiChat({ onCreateTicket }) {
  const { user } = useApp();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [limitHit, setLimitHit] = useState(false);
  const endRef = useRef();

  useEffect(() => { api.get("/ai/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); }, []);
  useEffect(() => { api.get("/ai/history").then((r) => { if (r.data?.length) setMessages(r.data.map((m) => ({ role: m.role, text: m.text, ticket: m.ticket }))); }).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (!cfg || !cfg.enabled) return null;

  const remaining = cfg.remaining;
  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    if (remaining <= 0) { setLimitHit(true); return; }
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const r = await api.post("/ai/chat", { message: q });
      setMessages((m) => [...m, { role: "ai", text: r.data.reply, ticket: r.data.suggest_ticket }]);
      setCfg((c) => ({ ...c, remaining: r.data.remaining }));
      if (r.data.remaining <= 0) setLimitHit(true);
    } catch (e) {
      if (e.response?.status === 429) { setLimitHit(true); setCfg((c) => ({ ...c, remaining: 0 })); }
      else setMessages((m) => [...m, { role: "ai", text: e.response?.data?.detail || "Ich bin gerade nicht erreichbar. Bitte versuche es später." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="sb-card p-5" data-testid="ai-chat">
      <div className="flex items-center gap-2 mb-1">
        <div className="p-1.5 rounded-xl" style={{ background: "var(--app-primary)" }}><Sparkle size={20} weight="fill" className="text-white" /></div>
        <div>
          <h2 className="font-heading font-semibold">Safe Buddy AI</h2>
          <p className="font-body text-xs text-gray-500" data-testid="ai-remaining">Heute noch {remaining} {remaining === 1 ? "Frage" : "Fragen"}</p>
        </div>
      </div>

      <div className="space-y-2 mt-3 max-h-72 overflow-y-auto" data-testid="ai-messages">
        <div className="rounded-2xl bg-gray-50 border-2 border-gray-200 px-3 py-2 font-body text-sm text-gray-800" data-testid="ai-greeting">{cfg.greeting}</div>
        {messages.map((m, i) => (
          <div key={i} data-testid={`ai-msg-${i}`} className={`rounded-2xl px-3 py-2 font-body text-sm ${m.role === "user" ? "bg-[var(--app-primary)] text-white ml-8" : "bg-gray-50 border-2 border-gray-200 text-gray-800 mr-2"}`}>
            <p className="whitespace-pre-wrap">{m.text}</p>
            {m.ticket && (
              <button data-testid="ai-create-ticket" onClick={onCreateTicket} className="mt-2 sb-btn px-3 py-1.5 bg-white text-gray-800 text-xs font-medium flex items-center gap-1"><Lifebuoy size={14} />Support-Ticket erstellen</button>
            )}
          </div>
        ))}
        {busy && <div className="rounded-2xl bg-gray-50 border-2 border-gray-200 px-3 py-2 font-body text-sm text-gray-500 mr-2" data-testid="ai-typing">Safe Buddy AI denkt nach …</div>}
        <div ref={endRef} />
      </div>

      {remaining > 0 ? (
        <div className="flex gap-2 mt-3">
          <input data-testid="ai-input" className="sb-input" placeholder="Deine Frage …" value={input}
            onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} disabled={busy} />
          <button data-testid="ai-send" onClick={send} disabled={busy || !input.trim()} className="sb-btn px-4 text-white font-medium disabled:opacity-50 shrink-0" style={{ background: "var(--app-primary)" }}><PaperPlaneRight size={18} weight="fill" /></button>
        </div>
      ) : (
        <div className="mt-3 rounded-2xl border-2 border-amber-400 bg-amber-50 px-3 py-2.5" data-testid="ai-limit-hint">
          <p className="font-body text-sm text-gray-800">Du hast heute alle Fragen an Safe Buddy AI genutzt. Morgen kannst du wieder fragen. 🌙</p>
        </div>
      )}
      {limitHit && remaining > 0 && <p className="mt-2 font-body text-xs text-gray-400">Hinweis: Kurz warten zwischen den Fragen.</p>}
    </div>
  );
}
